import React, { useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Alert,
  Breadcrumbs,
  Link,
  useTheme,
  useMediaQuery,
  Grid,
  Paper,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Home as HomeIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import { MainLayout } from '../components/Layout/MainLayout';
import { ScoutEmailSettings } from '../components/ScoutSettings';
import { Loading } from '../components/UI/Loading';

export const StudentSettings: React.FC = () => {
  const { user, isLoading, error } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

  useEffect(() => {
    document.title = '設定 - ガクフドキャリア';
  }, []);

  if (isLoading) {
    return (
      <MainLayout showHeader={isMobile} title="設定">
        <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 4 } }}>
          <Loading variant="circular" size="large" />
        </Container>
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout showHeader={isMobile} title="設定">
        <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 4 } }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        </Container>
      </MainLayout>
    );
  }

  if (!user) {
    return (
      <MainLayout showHeader={isMobile} title="設定">
        <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 4 } }}>
          <Alert severity="warning" sx={{ mb: 3 }}>
            ユーザー情報が見つかりません。再度ログインしてください。
          </Alert>
        </Container>
      </MainLayout>
    );
  }

  // 学生のみアクセス可能
  if (user.userRole !== 'student') {
    return (
      <MainLayout showHeader={isMobile} title="設定">
        <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 4 } }}>
          <Alert severity="warning" sx={{ mb: 3 }}>
            この機能は学生のみ利用できます。
          </Alert>
        </Container>
      </MainLayout>
    );
  }

  return (
    <MainLayout showHeader={isMobile} title="設定">
      <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 4 } }}>
        {/* Breadcrumbs */}
        <Breadcrumbs
          aria-label="breadcrumb"
          sx={{
            mb: { xs: 2, sm: 3 },
            fontSize: { xs: '0.8rem', sm: '0.875rem' },
          }}
        >
          <Link
            underline="hover"
            sx={{
              display: 'flex',
              alignItems: 'center',
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
            }}
            color="inherit"
            href="/"
          >
            <HomeIcon
              sx={{ mr: 0.5, fontSize: { xs: 'small', sm: 'inherit' } }}
            />
            ホーム
          </Link>
          <Link
            underline="hover"
            sx={{
              display: 'flex',
              alignItems: 'center',
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
            }}
            color="inherit"
            href="/mypage"
          >
            <PersonIcon
              sx={{ mr: 0.5, fontSize: { xs: 'small', sm: 'inherit' } }}
            />
            マイページ
          </Link>
          <Typography
            sx={{
              display: 'flex',
              alignItems: 'center',
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
            }}
            color="text.primary"
          >
            <SettingsIcon
              sx={{ mr: 0.5, fontSize: { xs: 'small', sm: 'inherit' } }}
            />
            設定
          </Typography>
        </Breadcrumbs>

        {/* Page Header */}
        <Box sx={{ mb: { xs: 3, sm: 4 } }}>
          <Typography
            variant="h3"
            component="h1"
            gutterBottom
            sx={{
              fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
              fontWeight: 'bold',
            }}
          >
            設定
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}
          >
            アカウント設定とプライバシー設定を管理できます。
          </Typography>
        </Box>

        {/* Settings Content */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <ScoutEmailSettings />
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                設定について
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: 'text.secondary', lineHeight: 1.6 }}
              >
                こちらの設定ページでは、スカウトメールの受信設定を管理できます。
                設定はいつでも変更可能で、即座に反映されます。
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </MainLayout>
  );
};
